import * as THREE from 'three';

// Cinematic space environment: a procedural IBL env map (so PBR metal has
// something to reflect — the single biggest upgrade for MeshStandardMaterial)
// plus a hard sun key + cool rim light, matching the concept-art look
// (hard single sun, deep shadow, cool edge light, near-black space).
//
// Self-contained: no external HDRI file. The env is painted to a canvas as an
// equirectangular map and prefiltered with PMREMGenerator.

export interface SpaceEnv {
  envMap: THREE.Texture;
  sun: THREE.DirectionalLight;
  rim: THREE.DirectionalLight;
  dispose: () => void;
}

function paintEquirect(): THREE.CanvasTexture {
  const w = 1024, h = 512;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d')!;

  // base: near-black space with a faint cool vertical gradient
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0.0, '#0a1422');
  g.addColorStop(0.5, '#05080f');
  g.addColorStop(1.0, '#020306');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  // faint cool nebula glow (rim-light source), left-back
  const neb = ctx.createRadialGradient(w * 0.22, h * 0.45, 0, w * 0.22, h * 0.45, w * 0.28);
  neb.addColorStop(0, 'rgba(70,120,210,0.55)');
  neb.addColorStop(1, 'rgba(70,120,210,0)');
  ctx.fillStyle = neb;
  ctx.fillRect(0, 0, w, h);

  // the SUN — a hard, bright warm disc (the key). Right side, slightly high.
  const sx = w * 0.74, sy = h * 0.36;
  const halo = ctx.createRadialGradient(sx, sy, 0, sx, sy, w * 0.2);
  halo.addColorStop(0, 'rgba(255,244,214,1)');
  halo.addColorStop(0.12, 'rgba(255,236,190,0.9)');
  halo.addColorStop(0.4, 'rgba(255,180,90,0.25)');
  halo.addColorStop(1, 'rgba(255,160,70,0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#fffdf6';
  ctx.beginPath();
  ctx.arc(sx, sy, w * 0.022, 0, Math.PI * 2);
  ctx.fill();

  const tex = new THREE.CanvasTexture(c);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// Just the IBL env map (no lights) — for scenes that already have a light rig
// (the live game uses scene/lighting.ts). This is the genuinely-valuable part
// lighting.ts lacks: something for PBR metal to reflect. Per PR #8 review #6.
export function createEnvMap(renderer: THREE.WebGLRenderer): THREE.Texture {
  const equirect = paintEquirect();
  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const envMap = pmrem.fromEquirectangular(equirect).texture;
  equirect.dispose();
  pmrem.dispose();
  return envMap;
}

export function createSpaceEnv(renderer: THREE.WebGLRenderer, scene: THREE.Scene): SpaceEnv {
  const envMap = createEnvMap(renderer);
  scene.environment = envMap;
  scene.environmentIntensity = 0.55; // reflections present but the sun stays the hero

  // Hard sun key — direction matches the painted sun (right, slightly high, front).
  const sun = new THREE.DirectionalLight(0xfff2d8, 3.6);
  sun.position.set(120, 70, 90);
  scene.add(sun);

  // Cool rim/back light — edges the hull against black (the concept-art separation).
  const rim = new THREE.DirectionalLight(0x5e8cff, 1.3);
  rim.position.set(-90, 20, -140);
  scene.add(rim);

  // Very low ambient so shadows stay deep but not pure black.
  scene.add(new THREE.AmbientLight(0x2a3550, 0.18));

  // Bounce fill (the lighting game, opener) — steep chamfer facets that face
  // neither key nor rim read as pure-black stripes without it (the roci-study
  // skin-pass lesson). A dim warm-under/cool-over hemisphere stands in for
  // light scattered off the ship's own structure.
  scene.add(new THREE.HemisphereLight(0x33405a, 0x2b2118, 0.5));

  return {
    envMap, sun, rim,
    dispose: () => { envMap.dispose(); },
  };
}
